/**
 * Copyright ToolBelt Data Inc., 2020 All Rights Reserved
 */

// 1. React & Third-party Libraries
import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from "react-redux";
import { Col, Image, Row } from 'react-bootstrap';

// 2. FontAwesome
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faEnvelope,
    faGlobe,
    faMapMarkerAlt,
    faPhone,
} from '@fortawesome/free-solid-svg-icons';

// 3. Global App Config & Services
import mapMarkerIcon from '../../../../common/assets/img/ToolbeltIcons-15.png';
import { APP_COLORS } from '../../../../common/Constants';
import { SelectStyled } from "../../../../common/components/form/FormSelect";
import { getSupplierRecordByCompanyRecordId } from '../../../../common/services/SupplierService';

// 4. Redux Slices & Filters
import { setFilterValue } from '../../universeSlice';
import { countUniverse, searchUniverse } from '../../universeQuerySlice';
import { hiddenFilters } from "../../hiddenFilters";

// 5. Local Components
import ExploreMapFlyoutDetailLoadingSpinner from './ExploreMapFlyoutDetailLoadingSpinner';
import { ExploreMapFlyoutSelectedDetailSection } from './ExploreMapFlyoutSelectedDetailSection';

const options = [
    { value: 5, label: '5 Miles' },
    { value: 10, label: '10 Miles' },
    { value: 20, label: '20 Miles' },
    { value: 50, label: '50 Miles' },
    { value: 100, label: '100 Miles' },
    { value: 200, label: '200 Miles' },
];

export default function ExploreMapFlyoutDetailSelectedSupplier(props) {
    const dispatch = useDispatch();

    const universeState = useSelector(state => state.universe);
    const currentUniverse = universeState.currentUniverse || 'BUSINESS';
    const targetUniverse = universeState.universes[currentUniverse];

    const [loading, setLoading] = useState(true);
    const [selectedRecord, setSelectedRecord] = useState(null);
    const [selectedDistanceFrom, setSelectedDistanceFrom] = useState(null);

    const { selectedPoint } = props;

    useEffect(() => {
        if (!selectedPoint) return;

        setLoading(true);
        _getRecord(selectedPoint)
            .then((response) => {
                setLoading(false);
                setSelectedRecord(response);
            });
    }, [selectedPoint]);

    const handleSearch = async (radiusOption, recordData) => {
        if (!radiusOption || !recordData) return;

        const { companyAddressLatitude, companyAddressLongitude } = recordData;

        const geoConfig = hiddenFilters?.BUSINESS?.COMPANY_ADDRESS_GEO_RADIUS
            || hiddenFilters?.COMPANY?.COMPANY_ADDRESS_GEO_RADIUS
            || hiddenFilters?.COMPANY_ADDRESS_GEO_RADIUS
            || {};

        const geoFilter = {
            id: geoConfig.id || 'COMPANY_ADDRESS_GEO_RADIUS',
            field: geoConfig.field || 'CompanyContractorUniverse.company_address_geo_radius',
            operatorId: geoConfig.operatorId || 'geoRadius',
            ...geoConfig,
            value: {
                latitude: Number(companyAddressLatitude),
                longitude: Number(companyAddressLongitude),
                radius: Number(radiusOption.value),
            }
        };

        dispatch(setFilterValue({
            id: geoFilter.id,
            ...geoFilter
        }));

        dispatch(countUniverse({
            universeKey: currentUniverse,
            isMapEnabled: targetUniverse.toggleMap
        }));

        dispatch(searchUniverse({
            universeKey: currentUniverse
        }));
    };

    if (loading) return (<ExploreMapFlyoutDetailLoadingSpinner />);

    if (selectedRecord) {
        const {
            companyName,
            companyAddressDeliveryLine1,
            companyAddressLastLine,
            companyAddressCity,
            companyAddressRegion,
            companyAddressPostalCode,
            companyUrl
        } = selectedRecord;

        const cityRegion = [companyAddressCity, companyAddressRegion].filter(Boolean).join(', ');
        const locationAddressLastLine = [cityRegion, companyAddressPostalCode].filter(Boolean).join(' ');

        const densityIcons = [
            { attr: 'companyUrl', icon: faGlobe },
            { attr: 'companyEmail', icon: faEnvelope },
            { attr: 'companyPhone', icon: faPhone },
            { attr: 'companyAddressDeliveryLine1', icon: faMapMarkerAlt },
        ];

        const densityIconsJsx = densityIcons.map((i, idx) => {
            if (selectedRecord[i.attr] && selectedRecord[i.attr] !== 'null') {
                const marginLeft = idx === 0 ? '0px' : '10px';

                if (i.attr === 'companyUrl') {
                    return (
                        <div key={idx} style={{ marginLeft }}>
                            <a href={`${companyUrl}`} target="_blank" rel="noreferrer">
                                <FontAwesomeIcon color={APP_COLORS['orange']} icon={i.icon} style={{ fontSize: '16px' }} />
                            </a>
                        </div>
                    );
                }

                return (
                    <div key={idx} style={{ marginLeft }}>
                        <FontAwesomeIcon color={APP_COLORS['blue']} icon={i.icon} style={{ fontSize: '16px' }} />
                    </div>
                );
            }
            return null;
        }).filter(Boolean);

        return (
            <Row>
                <Col>
                    <div className="explore-map-flyout-detail">
                        <ExploreMapFlyoutSelectedDetailSection
                            name={companyName}
                            address1={companyAddressDeliveryLine1}
                            address2={locationAddressLastLine || companyAddressLastLine}
                            densityIconsJsx={densityIconsJsx}
                            detailIconJsx={
                                <Image
                                    className="icon"
                                    src={mapMarkerIcon}
                                    alt="ToolBeltData Map Marker Icon"
                                    fluid
                                />
                            }
                        />
                        <div className="flyout-map-tools-section">
                            <div className="label-text">SEARCH WITHIN</div>
                            <SelectStyled
                                id="selected-location-point-geospatial-search-typeahead"
                                options={options}
                                onChange={(e) => {
                                    setSelectedDistanceFrom(e);
                                    handleSearch(e, selectedRecord);
                                }}
                                value={selectedDistanceFrom}
                                placeholder={'Select a distance ...'}
                                menuPortalTarget={document.body}
                                menuPosition={'absolute'}
                                styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
                            />
                        </div>
                    </div>
                </Col>
            </Row>
        );
    }
    return null;
}

ExploreMapFlyoutDetailSelectedSupplier.propTypes = {
    selectedPoint: PropTypes.object,
};

// --- Helper Functions ---
async function _getRecord(selectedPoint) {
    const { sourceId, companyRecordId } = selectedPoint.properties;
    const record = await getSupplierRecordByCompanyRecordId(sourceId, companyRecordId);
    return record && record.length > 0 ? record[0] : null;
}